/**
 * Static i18n system — loads translations from JSON locale files.
 * No OpenAI calls. No async. No 429 errors. SSR-safe.
 */

import en from '@/locales/en.json';
import pt from '@/locales/pt.json';
import es from '@/locales/es.json';

export type SupportedLocale = 'en' | 'pt' | 'es';

export const SUPPORTED_LANGUAGES: { code: SupportedLocale; label: string; name: string }[] = [
  { code: 'en', label: '🇺🇸 English', name: 'English' },
  { code: 'pt', label: '🇧🇷 Português', name: 'Portuguese' },
  { code: 'es', label: '🇪🇸 Español', name: 'Spanish' },
];

/** Maps BCP-47 code → locale data */
const locales: Record<SupportedLocale, typeof en> = { en, pt, es };

/** Maps legacy language name strings → locale code */
export const LANGUAGE_NAME_TO_CODE: Record<string, SupportedLocale> = {
  English: 'en',
  Portuguese: 'pt',
  Spanish: 'es',
};

/** Maps locale code → language name */
export const LANGUAGE_MAP: Record<string, string> = {
  en: 'English',
  pt: 'Portuguese',
  es: 'Spanish',
  // Extended for browser detection fallback
  fr: 'French',
  de: 'German',
  it: 'Italian',
  ar: 'Arabic',
  ja: 'Japanese',
  ko: 'Korean',
  zh: 'Chinese',
  ru: 'Russian',
};

export type Translations = typeof en;

/**
 * Get the full translations object for a locale code.
 * Falls back to English for unsupported locales.
 */
export function getTranslations(code: string): Translations {
  const locale = code as SupportedLocale;
  return locales[locale] ?? en;
}

/**
 * Translate a flat map of UI strings using static locale data.
 * Looks up each value in the locale JSON by searching all sections.
 * Falls back to the original English string if not found.
 *
 * This is synchronous and SSR-safe — no API calls.
 */
export function translateStringsStatic(
  strings: Record<string, string>,
  code: string
): Record<string, string> {
  if (code === 'en' || !locales[code as SupportedLocale]) return strings;

  const t = getTranslations(code);
  const result: Record<string, string> = {};

  for (const [key, value] of Object.entries(strings)) {
    // Search all sections for a matching value
    let found = false;
    for (const section of Object.values(t) as Record<string, string>[]) {
      if (typeof section === 'object') {
        for (const [, translated] of Object.entries(section)) {
          // Match by looking up the English equivalent
          const enSection = Object.values(en).find(
            (s) => typeof s === 'object' && Object.values(s as Record<string, string>).includes(value)
          ) as Record<string, string> | undefined;
          if (enSection) {
            const enKey = Object.keys(enSection).find((k) => enSection[k] === value);
            if (enKey && typeof section === 'object' && enKey in section) {
              result[key] = (section as Record<string, string>)[enKey];
              found = true;
              break;
            }
          }
        }
        if (found) break;
      }
    }
    if (!found) result[key] = value;
  }

  return result;
}
